import React, { useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import "./account.css";
import "./accountPage.css";
import { CombinedContext } from "../../context/combinedContext";
import {
    ArrowLeft,
    Bell,
    CalendarCheck,
    ChevronRight,
    CreditCard,
    Gift,
    Info,
    Languages,
    LayoutGrid,
    LifeBuoy,
    LogOut,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import translations from "../../translations/translations";

const BASE_MENU = [
    { id: "billing",        roles: ["owner"], order: 1, group: "business", icon: CreditCard },
    { id: "category",       roles: ["owner"], order: 2, group: "business", icon: LayoutGrid },
    { id: "bookings",       roles: ["customer"], order: 3, group: "business", icon: CalendarCheck },
    { id: "language",       roles: ["customer", "staff", "admin", "owner"], order: 4, group: "preferences", icon: Languages },
    { id: "notifications",  roles: ["customer", "staff", "admin", "owner"], order: 5, group: "preferences", icon: Bell },
    { id: "security",       roles: ["customer", "staff", "admin", "owner"], order: 6, group: "account", icon: ShieldCheck },
    { id: "helpandsupport", roles: ["customer", "staff", "admin", "owner"], order: 7, group: "support", icon: LifeBuoy },
    { id: "about",          roles: ["customer", "staff", "admin", "owner"], order: 8, group: "support", icon: Info },
];

const GROUP_ORDER = ["business", "preferences", "account", "support"];

/* Subpages reachable inside the section but not listed in the menu —
   the compact header still needs a title for them. */
const EXTRA_TITLES = {
    referral: "referral",
    contact: "contact",
    privacy: "privacy",
    terms: "terms",
    refund: "refund",
    favorites: "favorites",
    reviews: "reviews",
};

const AccountPage = () => {
    const { userData, language, logout } = useContext(CombinedContext);
    const user = userData?.user || {};
    const role = user?.role;

    const navigate = useNavigate();
    const location = useLocation();

    const t = translations[language]?.account || {};
    const currentTab = location.pathname.split("/")[2] || null;
    const isRootAccount = location.pathname === "/account";

    const initial = (user.firstName || user.email || "?").trim().charAt(0).toUpperCase();
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");

    // -------------------------------------------------------------
    // UNIFIED MENU, grouped
    // -------------------------------------------------------------
    const menuItems = BASE_MENU.filter((item) => item.roles.includes(role))
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
            ...item,
            label: t.menu?.[item.id] || item.id,
            hint: t.menuHints?.[item.id] || null,
        }));

    const groups = GROUP_ORDER.map((group) => ({
        id: group,
        label: t.groups?.[group] || "",
        items: menuItems.filter((item) => item.group === group),
    })).filter((group) => group.items.length > 0);

    const showReferral = role === "owner" || role === "staff" || role === "admin";

    const headerTitle =
        menuItems.find((x) => x.id === currentTab)?.label ||
        t.menu?.[EXTRA_TITLES[currentTab]] ||
        (currentTab === "faq" ? t.faq?.title || "FAQ" : null) ||
        t.header?.title ||
        "Account";

    const handleLogout = () => {
        logout();
        navigate("/signin");
    };

    return (
        <div className="account-container">
            {/* ── SIDEBAR / HUB ── */}
            <aside className={`account-sidebar ${!isRootAccount ? "hide-mobile" : ""}`}>
                {/* IDENTITY */}
                <div className="ap-identity">
                    <div className="ap-identity-cover" />

                    {/* The role pill sits on the cover so the name and email
                        get the card's full width instead of sharing a row. */}
                    {role && (
                        <span className="ap-identity-role">
                            <span className="ap-identity-role-dot" />
                            {t.roles?.[role] || role}
                        </span>
                    )}

                    <div className="ap-identity-body">
                        <div className="ap-avatar">
                            {user.photo ? <img src={user.photo} alt="" /> : <span className="ap-avatar-fallback">{initial}</span>}
                        </div>

                        <div className="ap-identity-copy">
                            <div className="ap-identity-name">{fullName || t.header?.title || "Account"}</div>
                            {user.email && <div className="ap-identity-email">{user.email}</div>}
                        </div>
                    </div>
                </div>

                {/* ⭐ FEATURED REFERRAL CARD */}
                {showReferral && (
                    <button
                        className={`ap-referral ${currentTab === "referral" ? "is-active" : ""}`}
                        onClick={() => navigate("/account/referral")}
                    >
                        <span className="ap-referral-icon">
                            <Gift size={20} />
                        </span>

                        <span className="ap-referral-copy">
                            <span className="ap-referral-title">
                                {t.menu?.referral || "Referral"}
                                <span className="ap-referral-tag">
                                    <Sparkles size={11} />
                                    {t.referralBadge || "$15"}
                                </span>
                            </span>
                            <span className="ap-referral-sub">
                                {t.referralSubtitle || "Invite other businesses and earn rewards"}
                            </span>
                        </span>

                        <ChevronRight size={18} className="ap-referral-chevron" />
                    </button>
                )}

                {/* MENU */}
                {groups.map((group) => (
                    <div key={group.id} className="ap-group">
                        {group.label && <p className="ac-label ap-group-label">{group.label}</p>}

                        <div className="ac-card">
                            <div className="ac-list">
                                {group.items.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`ac-row ${currentTab === item.id ? "is-active" : ""}`}
                                            onClick={() => {
                                                if (item.comingSoon) return;
                                                navigate(`/account/${item.id}`);
                                            }}
                                        >
                                            <span className="ac-icon">
                                                <Icon size={16} />
                                            </span>

                                            <span className="ac-row-copy">
                                                <span className="ac-row-label">{item.label}</span>
                                                {item.hint && <span className="ac-row-hint">{item.hint}</span>}
                                            </span>

                                            {item.comingSoon ? (
                                                <span className="ac-row-tag">{t.comingSoon || "Coming soon"}</span>
                                            ) : (
                                                <ChevronRight size={17} className="ac-row-chevron" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}

                {/* LOG OUT */}
                <div className="ac-card ap-logout-card">
                    <div className="ac-list">
                        <button type="button" className="ac-row is-danger" onClick={handleLogout}>
                            <span className="ac-icon is-danger">
                                <LogOut size={16} />
                            </span>
                            <span className="ac-row-copy">
                                <span className="ac-row-label">{t.menu?.logout || "Log Out"}</span>
                            </span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* ── COMPACT HEADER (narrow viewports, subpages only) ── */}
            {!isRootAccount && (
                <div className="account-mobile-header">
                    <button onClick={() => navigate(-1)} className="mobile-back-btn" aria-label={t.header?.back || "Back"}>
                        <ArrowLeft size={19} />
                    </button>
                    <h3>{headerTitle}</h3>
                </div>
            )}

            {/* ── MAIN CONTENT ── */}
            <main className="account-content">
                {isRootAccount ? (
                    <div className="ap-placeholder">
                        <span className="ac-icon">
                            <Sparkles size={18} />
                        </span>
                        <h2>{t.emptyState?.title}</h2>
                        <p>{t.emptyState?.description || t.emptyState?.subtitle}</p>
                    </div>
                ) : (
                    <Outlet />
                )}
            </main>
        </div>
    );
};

export default AccountPage;
